/**
 * @file FontRoleSection.tsx
 * @description Одна роль во вкладке «Шрифты» (phase19.16; SB10, дополнение
 * 2026-10-11; uiux-fonts-usage §3, §6): группа-карточка (SB122,
 * `SettingsGroup`, id = ключ роли) — название роли (у необязательной —
 * «· необязательно»), строка назначения, карточка шрифта, строка «Где: …»
 * (три самых заметных места), раскрывающийся список «Все места · N» и
 * настройки роли ниже (начертание, регистр, надписи капсом). У свёрнутой —
 * сводка: имя шрифта.
 *
 * Пришли из подсказки элемента с `?font=<роль>` (`focused`) — список «Все
 * места» сразу раскрыт. Раскрытие группы, прокрутку и подсветку делает
 * `openGroup` во `FontsTab` (штатная подсветка карточки группы).
 */

import { FONT_ROLES, type FontRole } from "@chaiberi/shared";
import { useEffect, useState, type ReactNode } from "react";

import { SettingsGroup, type SettingsGroupStatus } from "../../ui/settings-groups";
import { FontUsageList } from "./FontUsageList";

interface Props {
  role: FontRole;
  /** Карточка шрифта роли (`FontRoleCard`). */
  card: ReactNode;
  /** Сводка свёрнутой группы: «Montserrat · 700». */
  summary?: string;
  status?: SettingsGroupStatus;
  /** Роль не выбрана и вместо неё стоит шрифт этой роли (акцент → заголовков). */
  fallbackRole?: FontRole | null;
  /** Строка под «Где:» (надписи капсом — тоже этим шрифтом). */
  note?: string | null;
  /** Роль открыта по адресу `?font=` — список «Все места» раскрыт. */
  focused?: boolean;
  /** Настройки роли под списком мест. */
  children?: ReactNode;
}

export function FontRoleSection({ role, card, summary, status, fallbackRole = null, note = null, focused = false, children }: Props) {
  const info = FONT_ROLES[role];
  const [usageOpen, setUsageOpen] = useState(focused);

  // Переход с этой ролью в адресе — раскрыть «Все места» (свернуть потом можно самому).
  useEffect(() => {
    if (focused) setUsageOpen(true);
  }, [focused]);

  const fallbackText = fallbackRole ? ` — сейчас шрифтом ${FONT_ROLES[fallbackRole].genitive}` : "";

  return (
    <SettingsGroup
      id={role}
      title={info.optional ? `${info.title} · необязательно` : info.title}
      tocLabel={info.title}
      hint={info.purpose}
      summary={summary}
      status={status}
    >
      {card}
      {info.fallbackNote && !fallbackRole ? (
        <p className="type-caption break-words text-on-surface-variant">{capitalize(info.fallbackNote)}</p>
      ) : null}
      <p className="type-caption break-words text-on-surface-variant">
        Где: {info.brief.join(", ")}
        {fallbackText}
      </p>
      {note ? <p className="type-caption break-words text-on-surface-variant">{note}</p> : null}
      <FontUsageList role={role} open={usageOpen} onOpenChange={setUsageOpen} />
      {children}
    </SettingsGroup>
  );
}

/** «без него — шрифт заголовков» → «Без него — шрифт заголовков». */
function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}
